import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, FileText, Database, CheckCircle2, Layers, BarChart3, Download } from 'lucide-react';
import { FAIRFAX_TOP_FINDINGS, JURISDICTIONS } from '../data/vdot.js';
import { fmt, Pill, SectionHeader } from '../components/primitives.jsx';

const APCI_BREAKDOWN = [
  { label: 'Curb ramps (30% weight)',         score: 58 },
  { label: 'Sidewalk segments (30% weight)',  score: 65 },
  { label: 'Crosswalks (15% weight)',         score: 72 },
  { label: 'Transit stops (10% weight)',      score: 55 },
  { label: 'Pedestrian signals (10% weight)', score: 68 },
  { label: 'Parking spaces (5% weight)',      score: 60 },
];

export default function Report() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const slug = params.get('slug') || 'fairfax_county_va';
  const j = JURISDICTIONS.find((x) => x.slug === slug) || JURISDICTIONS[0];

  const downloadDocx = () => {
    // Use prod backend in production, vite /api proxy in local dev
    const isProd = typeof window !== 'undefined' &&
                   window.location.hostname !== 'localhost' &&
                   window.location.hostname !== '127.0.0.1';
    const base = isProd ? 'https://api.atlasgeo.ai' : '';
    window.location.href = `${base}/atlaspath-demo/report/${j.slug}.docx`;
  };

  return (
    <div className="space-y-6">
      <div className="bg-[#0a0a0a] text-white p-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#DBC078]/5 rounded-full blur-3xl translate-x-32"></div>
        <div className="relative">
          <div className="flex items-center justify-between mb-4">
            <Pill variant="gold">Sample tenant report</Pill>
            <button onClick={() => nav('/dashboard')} className="text-xs text-stone-300 hover:text-white">← Back to dashboard</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
            <div className="md:col-span-2">
              <h1 className="text-4xl mb-2 font-serif-display">{j.name} · ADA Compliance Assessment</h1>
              <div className="text-stone-300 text-sm">VDOT District: {j.district} · Maint Jurisdctn {j.code} · Reporting period: Q2 2026</div>
              <div className="text-xs text-stone-400 mt-2 space-x-3">
                <span>Prepared by AtlasGeo Technologies LLC (SDVOSB)</span>
                <span>·</span>
                <span>Report ID: <span className="font-mono">AP-{j.code}-2026Q2-001</span></span>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur border border-white/20 p-5 text-center">
              <div className="text-xs uppercase tracking-wider text-stone-300 mb-1">APCI score</div>
              <div className="text-6xl text-[#DBC078] font-serif-display">{j.apci}</div>
              <div className="text-sm text-stone-300 mt-1">{j.apci_category} · 0–100 scale</div>
            </div>
          </div>
          <div className="mt-6">
            <button onClick={downloadDocx} className="bg-[#DBC078] hover:bg-[#c4a85a] text-[#0a0a0a] font-medium px-4 py-2 text-sm uppercase tracking-wider inline-flex items-center gap-2">
              <Download size={14} /> Download PROWAG Assessment DOCX
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-stone-200 p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">Inventory</div>
            <div className="text-3xl text-[#0a0a0a] font-serif-display">{fmt(j.count)}</div>
            <div className="text-xs text-stone-500 mt-1">Curb ramps in {j.name} VDOT ROW</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">Noncompliant</div>
            <div className="text-3xl text-[#7A1F2B] font-serif-display">{fmt(j.noncompliant)}</div>
            <div className="text-xs text-stone-500 mt-1">{(j.noncompliant/j.count*100).toFixed(1)}% across Grades B + C + D</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">Remediation estimate</div>
            <div className="text-3xl text-[#0a0a0a] font-serif-display">${j.cost_low_m}–${j.cost_high_m}M</div>
            <div className="text-xs text-stone-500 mt-1">Upper bound; overlap not deduped</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">VDOT alignment</div>
            <div className="text-3xl text-[#1F7A8C] font-serif-display">{j.vdot_align}%</div>
            <div className="text-xs text-stone-500 mt-1">AtlasPath agreement with VDOT grades</div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-stone-200 p-6">
        <SectionHeader sub="Per-asset-class scoring per CLAUDE.md Part 10.4">APCI breakdown</SectionHeader>
        <div className="space-y-3">
          {APCI_BREAKDOWN.map((el) => (
            <div key={el.label}>
              <div className="flex justify-between text-sm mb-1.5">
                <span className="text-stone-700">{el.label}</span>
                <span className="font-mono text-stone-700">
                  <span className="text-[#0a0a0a] font-medium">{el.score}</span>
                </span>
              </div>
              <div className="h-2 bg-stone-100 overflow-hidden">
                <div className={el.score >= 75 ? 'h-full bg-[#1F7A8C]' : el.score >= 60 ? 'h-full bg-[#DBC078]' : 'h-full bg-[#7A1F2B]'} style={{ width: el.score + '%' }}></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-stone-200 p-6">
        <SectionHeader sub="Per-ramp dual-citation: AtlasPath calibrated measurement + VDOT inventory grade">
          Top priority findings · sample
        </SectionHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b-2 border-stone-200">
              <tr className="text-xs uppercase tracking-wider text-stone-500">
                <th className="text-left py-3 pr-4">Ramp ID</th>
                <th className="text-left py-3 pr-4">Location</th>
                <th className="text-left py-3 pr-4">AP grade</th>
                <th className="text-left py-3 pr-4">VDOT grade</th>
                <th className="text-left py-3 pr-4 max-w-md">Finding</th>
                <th className="text-right py-3 pl-4">Est. cost</th>
              </tr>
            </thead>
            <tbody>
              {FAIRFAX_TOP_FINDINGS.map((f) => (
                <tr key={f.id} className="border-b border-stone-100 hover:bg-stone-50">
                  <td className="py-3 pr-4 font-mono text-xs text-[#0a0a0a]">{f.id}</td>
                  <td className="py-3 pr-4 text-stone-700 text-xs">{f.loc}</td>
                  <td className="py-3 pr-4"><Pill variant={f.ap_grade === 'A' ? 'success' : f.ap_grade === 'D' ? 'burgundy' : 'gold'}>{f.ap_grade}</Pill></td>
                  <td className="py-3 pr-4"><Pill variant={f.vdot_grade === 'A' ? 'success' : f.vdot_grade === 'D' ? 'burgundy' : 'gold'}>{f.vdot_grade}</Pill></td>
                  <td className="py-3 pr-4 text-stone-600 text-xs max-w-md">{f.issue}</td>
                  <td className="py-3 pl-4 text-right font-mono text-xs">{f.cost ? '$' + fmt(f.cost) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 text-xs text-stone-500 italic">
          Sample shows 5 of {fmt(j.noncompliant)} noncompliant findings. Full report includes per-ramp photo, PLY point cloud reference, PROWAG citation, and remediation recommendation.
        </div>
      </div>

      <div className="bg-stone-50 border-2 border-[#DBC078]/40 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Shield size={20} className="text-[#0a0a0a]" />
          <h2 className="text-lg text-[#0a0a0a] font-serif-display">Regulatory authority for this report</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
          <div>
            <div className="font-medium text-[#0a0a0a] uppercase tracking-wider mb-2">Federal authority</div>
            <ul className="space-y-1 text-stone-700">
              <li>· Title II of the ADA · 42 U.S.C. §12131 et seq.</li>
              <li>· 28 CFR Part 35 (DOJ Title II regulations)</li>
              <li>· 28 CFR §35.150(d) · Transition Plan</li>
              <li>· PROWAG R3 · 88 FR 53604 (Sept 7, 2023)</li>
              <li>· USDOT Final Rule · 89 FR 84528 (Jan 17, 2025)</li>
              <li>· 49 CFR Parts 37 &amp; 38</li>
            </ul>
          </div>
          <div>
            <div className="font-medium text-[#0a0a0a] uppercase tracking-wider mb-2">Virginia state authority</div>
            <ul className="space-y-1 text-stone-700">
              <li>· Va. Code §2.2-3500 et seq. (ITAA, am. HB 2541)</li>
              <li>· VDOT IIM-LD-55 (Pedestrian Facility Design)</li>
              <li>· VDOT IIM-TE-376.1 (rev. April 2024)</li>
              <li>· VDOT ADA Transition Plan (2019, rev. 2023)</li>
              <li>· VDOT Standard Drawings CG-2, 11, 12, 13</li>
            </ul>
          </div>
          <div>
            <div className="font-medium text-[#0a0a0a] uppercase tracking-wider mb-2">Measurement methodology</div>
            <ul className="space-y-1 text-stone-700">
              <li>· Colorado DOT Curb Ramp Measurement Guide v1.0</li>
              <li>· Ohio DOT Curb Ramp Evaluation Form</li>
              <li>· VTRC Report 21-R18 (March 2021, FHWA/VTRC)</li>
              <li>· NIST traceability policy</li>
            </ul>
          </div>
          <div>
            <div className="font-medium text-[#0a0a0a] uppercase tracking-wider mb-2">Verification reports on file</div>
            <ul className="space-y-1 text-stone-700">
              <li>· Calibration Validation Report · quarterly</li>
              <li>· Per-Capture Verifications · 10% spot-check</li>
              <li>· System Methodology Audit · annual</li>
              <li>· Third-party audit · pending Year 1</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-white border border-stone-200 p-6">
        <SectionHeader sub="Pilot deliverable bundle · ZIP archive">Included deliverables</SectionHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { name: 'ADA Transition Plan (DOCX)',                 desc: 'Full Title II §35.150(d) plan',           icon: FileText },
            { name: 'Asset Inventory Report (DOCX)',              desc: 'Per-element counts, top 25 priorities',   icon: Database },
            { name: 'PROWAG Compliance Assessment (DOCX)',        desc: 'Per-element findings + remediation',      icon: CheckCircle2 },
            { name: 'Inventory GeoJSON',                          desc: 'GIS layer for AGOL / ArcGIS Pro / QGIS',  icon: Layers },
            { name: 'Findings CSV',                               desc: 'Tabular export for ERP / asset mgmt',     icon: BarChart3 },
            { name: 'Calibration Appendix (PDF)',                 desc: 'NIST-traceable certs; per-capture',       icon: Shield },
          ].map((d) => (
            <div key={d.name} className="flex items-start gap-3 p-3 border border-stone-200 hover:bg-stone-50">
              <d.icon size={18} className="text-[#1F7A8C] mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#0a0a0a]">{d.name}</div>
                <div className="text-xs text-stone-500 mt-0.5">{d.desc}</div>
              </div>
              <Download size={14} className="text-stone-400 flex-shrink-0" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
